import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { ThemeToggle } from '../../context/ThemeContext';
import { useUnifiedAuth } from '../../context/UnifiedAuthContext';
import { Logger } from '../../utils/logger';

const log = Logger.create('ViewLayout');

/**
 * ViewLayout - Einheitliches Layout für ALLE Views
 * 
 * Struktur:
 * ┌──────────────────────────────────────────────────────────────┐
 * │ HEADER (sticky)                                               │
 * │ ┌──────────────────────────────────────────────────────────┐ │
 * │ │ [← Zurück]  Title                         [Actions] [⚙️] │ │
 * │ │             Subtitle                                     │ │
 * │ └──────────────────────────────────────────────────────────┘ │
 * ├──────────────────────────────────────────────────────────────┤
 * │ ACTION BAR (optional)                                        │
 * │ ┌──────────────────────────────────────────────────────────┐ │
 * │ │ [Tab1] [Tab2] [Tab3]  ODER  {actionBarContent}           │ │
 * │ └──────────────────────────────────────────────────────────┘ │
 * ├──────────────────────────────────────────────────────────────┤
 * │ CONTENT                                                      │
 * │                                                              │
 * │   {children}                                                 │
 * │                                                              │
 * └──────────────────────────────────────────────────────────────┘
 */
function ViewLayout({
  // Header
  title,
  subtitle,
  onBack,
  backLabel = '← Zurück',
  
  // Actions (rechts im Header)
  actions = [],
  primaryAction,
  showSettings = false,
  onSettingsClick,
  showLogout = false,
  
  // Action Bar (unter Header)
  tabs,
  activeTab,
  onTabChange,
  actionBarContent,
  
  // Content
  maxWidth = 'max-w-7xl',
  noPadding = false,
  children
}) {
  const navigate = useNavigate();
  const { logout } = useUnifiedAuth();

  const handleBack = () => {
    if (typeof onBack === 'function') {
      onBack();
    } else if (typeof onBack === 'string') {
      navigate(onBack);
    } else {
      navigate(-1);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (err) {
      log.error('Logout error', err);
    }
  };

  // Check if we have any action bar content
  const hasActionBar = tabs?.length > 0 || actionBarContent;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 theme-transition">
      {/* ========== HEADER ========== */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-20 theme-transition">
        <div className={`${maxWidth} mx-auto px-4 sm:px-6 lg:px-8`}>
          <div className="flex items-center justify-between h-16">
            {/* Left: Back + Title */}
            <div className="flex items-center gap-4 min-w-0 flex-1">
              {onBack !== undefined && (
                <button
                  onClick={handleBack}
                  className="flex-shrink-0 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg theme-transition"
                  title="Zurück"
                >
                  {backLabel}
                </button>
              )}
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-gray-900 dark:text-white truncate">
                  {title}
                </h1>
                {subtitle && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                    {subtitle}
                  </p>
                )}
              </div>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <ThemeToggle />
              
              {/* Secondary Actions */}
              {actions.map((action, index) => (
                <button
                  key={index}
                  onClick={action.onClick}
                  className={`px-3 py-2 text-sm rounded-lg theme-transition ${
                    action.variant === 'primary'
                      ? 'font-medium text-white bg-primary-600 hover:bg-primary-700'
                      : action.variant === 'success'
                      ? 'font-medium text-white bg-success-600 hover:bg-success-700'
                      : action.variant === 'danger'
                      ? 'font-medium text-white bg-danger-600 hover:bg-danger-700'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                  disabled={action.disabled}
                  title={action.title}
                >
                  {action.icon && <span className="mr-1.5">{action.icon}</span>}
                  {action.label}
                </button>
              ))}

              {/* Primary Action */}
              {primaryAction && (
                <button
                  onClick={primaryAction.onClick}
                  className={`px-4 py-2 text-sm font-medium rounded-lg theme-transition ${
                    primaryAction.variant === 'success'
                      ? 'text-white bg-success-600 hover:bg-success-700'
                      : primaryAction.variant === 'danger'
                      ? 'text-white bg-danger-600 hover:bg-danger-700'
                      : 'text-white bg-primary-600 hover:bg-primary-700'
                  }`}
                  disabled={primaryAction.disabled}
                >
                  {primaryAction.icon && <span className="mr-1.5">{primaryAction.icon}</span>}
                  {primaryAction.label}
                </button>
              )}

              {/* Settings Button */}
              {showSettings && (
                <button
                  onClick={onSettingsClick}
                  className="p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg theme-transition"
                  title="Einstellungen"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>
              )}

              {/* Logout */}
              {showLogout && (
                <button
                  onClick={handleLogout}
                  className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg theme-transition"
                >
                  Abmelden
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ========== ACTION BAR ========== */}
      {hasActionBar && (
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 theme-transition">
          <div className={`${maxWidth} mx-auto px-4 sm:px-6 lg:px-8`}>
            {/* Tabs */}
            {tabs && tabs.length > 0 && (
              <div className="flex gap-1 -mb-px">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => onTabChange?.(tab.id)}
                    className={`px-4 py-3 text-sm font-medium theme-transition border-b-2 ${
                      activeTab === tab.id
                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                        : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    {tab.icon && <span className="mr-1.5">{tab.icon}</span>}
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            {/* Custom Action Bar Content - shown BELOW tabs or alone */}
            {actionBarContent && (
              <div className={tabs && tabs.length > 0 ? 'py-3 border-t border-gray-200 dark:border-gray-700' : 'py-3'}>
                {actionBarContent}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========== CONTENT ========== */}
      <main className={noPadding ? '' : `${maxWidth} mx-auto px-4 sm:px-6 lg:px-8 py-8`}>
        {children}
      </main>
    </div>
  );
}

ViewLayout.propTypes = {
  // Header
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  onBack: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  backLabel: PropTypes.string,
  
  // Actions
  actions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    icon: PropTypes.node,
    variant: PropTypes.oneOf(['default', 'primary', 'success', 'danger']),
    disabled: PropTypes.bool,
    title: PropTypes.string
  })),
  primaryAction: PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    icon: PropTypes.node,
    variant: PropTypes.oneOf(['primary', 'success', 'danger']),
    disabled: PropTypes.bool
  }),
  showSettings: PropTypes.bool,
  onSettingsClick: PropTypes.func,
  showLogout: PropTypes.bool,
  
  // Action Bar
  tabs: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.node
  })),
  activeTab: PropTypes.string,
  onTabChange: PropTypes.func,
  actionBarContent: PropTypes.node,
  
  // Content
  maxWidth: PropTypes.oneOf(['max-w-6xl', 'max-w-7xl']),
  noPadding: PropTypes.bool,
  children: PropTypes.node
};

export default ViewLayout;
